import { ContainerElement } from '@/containerElement';
import { Heading3Element } from '@/headingElement';
import { ImageElement } from '@/imageElement';
import { ParagraphElement } from '@/paragraphElement';
import { SerializeTextLeaf } from '@/textLeaf';
import type { RSSFeedDataYouTube, RSSFeedDataYouTubeEntry } from '@/types';
import SimpleModal from '@/ui/SimpleModal';
import { getBackgroundColorStyle } from '@/utils/background';
import { cn } from '@/utils/cn';
import { type CSSProperties, type PropsWithChildren, useState } from 'react';
import * as styles from './RSSFeed.css';
import type { RSSFeedElementProps } from './RSSFeed.types';
import { YoutubeLogoSVG } from './YouTubeLogo';
import { formatDate } from './util';

const YOUTUBE_RSS_FEED_DEFAULTS = {
  imageBorderRadius: '8px',
  titleFontSize: 24,
  titleFontFamily: 'Inter',
  subtitleFontSize: 14,
  subtitleFontFamily: 'Inter',
  titleTextColor: 'inherit',
  subtitleTextColor: 'inherit',
  titleFontWeight: 700,
  subtitleFontWeight: 400,

  borderRadius: '8px',
  borderWidth: '1px',
  borderColor: 'inherit',
  borderStyle: 'none',
  background: 'transparent',
};

const COLUMNS_MAP: Record<string, string> = {
  '1': 'grid-cols-1',
  '2': 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-2',
  '3': 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3',
  '4': 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-4',
  '5': 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-5',
  '6': 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-6',
};
export const YouTubeRssFeedCard = (
  props: Omit<RSSFeedElementProps, 'children'> & { entry: RSSFeedDataYouTubeEntry },
) => {
  const { entry, element, onClick } = props;
  const {
    itemPadding,
    itemGap,
    imageBorderRadius,
    imageAspectRatio,
    imageFit,
    titleFontSize,
    titleFontFamily,
    titleTextColor,
    titleFontWeight,
    titleAlignment,
    subtitleFontSize,
    subtitleFontFamily,
    subtitleTextColor,
    subtitleFontWeight,
    subtitleAlignment,
    borderRadius,
    borderWidth,
    borderColor,
    borderStyle,
    backgroundColor,
    textGap,
    showPlayButton,
    playAction,
  } = element.attrs;

  const [isModalOpen, setIsModalOpen] = useState(false);
  return (
    <>
      <a
        href={entry.link?.['@href']}
        key={entry.id}
        className={cn('flex flex-col items-start cursor-pointer', styles.youtubeCard)}
        target="_blank"
        rel="noopener noreferrer"
        onClick={onClick}
        style={
          {
            padding: itemPadding,
            gap: itemGap || '8px',

            '--card-border-radius': borderRadius || YOUTUBE_RSS_FEED_DEFAULTS.borderRadius,
            '--card-border-width': borderWidth || YOUTUBE_RSS_FEED_DEFAULTS.borderWidth,
            '--card-border-color': borderColor || YOUTUBE_RSS_FEED_DEFAULTS.borderColor,
            '--card-border-style': borderStyle || YOUTUBE_RSS_FEED_DEFAULTS.borderStyle,
            ...getBackgroundColorStyle(backgroundColor),
          } as CSSProperties
        }
      >
        <ContainerElement
          element={{
            type: 'container',
            attrs: {
              id: 'image-container',
              padding: '0px',
              position: 'relative',
              backgroundColor: 'transparent',
              height: 'auto',
              borderRadius: imageBorderRadius || YOUTUBE_RSS_FEED_DEFAULTS.imageBorderRadius,
              overflow: 'hidden',
            },
            content: [],
          }}
          onClick={(e) => {
            if (playAction === 'modal') {
              e.preventDefault();
              e.stopPropagation();
              setIsModalOpen(true);
            }
            // If type link, it's already handled by the anchor tag
          }}
        >
          <ImageElement
            element={{
              type: 'image',
              attrs: {
                src: entry['media:group']?.['media:thumbnail']?.['@url'],
                width: '100%',
                height: 'auto',
                aspectRatio: imageAspectRatio || '16/9',
                objectFit: imageFit || 'cover',
              },
            }}
          />
          {showPlayButton && (
            <ContainerElement
              element={{
                type: 'container',
                attrs: {
                  id: 'play-button-container',
                  position: 'absolute',
                  top: '0',
                  left: '0',
                  padding: '0px',
                  backgroundColor: 'transparent',
                  width_max: '100%',
                  height: '100%',
                  zIndex: 10,
                },
                content: [],
              }}
            >
              <YoutubeLogoSVG width={64} height={64} />
            </ContainerElement>
          )}
        </ContainerElement>
        <ContainerElement
          element={{
            type: 'container',
            attrs: {
              id: 'container',
              padding: '0px',
              backgroundColor: 'transparent',
              gap: textGap || '8px',
            },
            content: [],
          }}
        >
          <Heading3Element
            element={{
              type: 'heading',
              attrs: {
                id: 'heading3',
                anchorId: 'heading3',
                padding: '0px',
                anchorTitle: 'heading3',
                anchorIdSync: true,
                anchorEnabled: true,
                anchorTitleSync: true,
                customAttributes: {},
                textAlign: titleAlignment || 'left',
                level: 3,
              },
              content: [
                {
                  text: entry.title,
                },
              ],
            }}
          >
            <SerializeTextLeaf
              leaf={{
                text: entry.title,
                marks: [
                  {
                    type: 'textStyle',
                    attrs: {
                      fontSize: titleFontSize || YOUTUBE_RSS_FEED_DEFAULTS.titleFontSize,
                      fontFamily: titleFontFamily || YOUTUBE_RSS_FEED_DEFAULTS.titleFontFamily,
                      color: titleTextColor || YOUTUBE_RSS_FEED_DEFAULTS.titleTextColor,
                      fontWeight: titleFontWeight || YOUTUBE_RSS_FEED_DEFAULTS.titleFontWeight,
                    },
                  },
                ],
              }}
            />
          </Heading3Element>
          <ParagraphElement
            element={{
              type: 'paragraph',
              attrs: {
                textAlign: subtitleAlignment || 'left',
                padding: '0px',
              },
            }}
          >
            <SerializeTextLeaf
              leaf={{
                text: formatDate(entry.published),
                marks: [
                  {
                    type: 'textStyle',
                    attrs: {
                      fontSize: subtitleFontSize || YOUTUBE_RSS_FEED_DEFAULTS.subtitleFontSize,
                      fontFamily: subtitleFontFamily || YOUTUBE_RSS_FEED_DEFAULTS.subtitleFontFamily,
                      color: subtitleTextColor || YOUTUBE_RSS_FEED_DEFAULTS.subtitleTextColor,
                      fontWeight: subtitleFontWeight || YOUTUBE_RSS_FEED_DEFAULTS.subtitleFontWeight,
                    },
                  },
                ],
              }}
            />
          </ParagraphElement>
        </ContainerElement>
      </a>
      <SimpleModal
        key={`${entry['yt:videoId']}-modal`}
        isOpen={isModalOpen}
        onClose={(value) => {
          setIsModalOpen(value);
        }}
        overlayClassName="bg-black/50"
      >
        <iframe
          width="560"
          height="315"
          src={`https://www.youtube.com/embed/${entry['yt:videoId']}`}
          title="YouTube video player"
          frameBorder="0"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
          referrerPolicy="strict-origin-when-cross-origin"
          allowFullScreen
        />
      </SimpleModal>
    </>
  );
};

export const YouTubeRssFeed = (props: PropsWithChildren<RSSFeedElementProps>) => {
  const { element, attributes, onClick, children, className } = props;
  const { data: rawData, isLoading, gap, columns, padding } = element.attrs;

  const showCount = element.attrs.showCount || 5;

  const data = rawData as RSSFeedDataYouTube;
  const shownData = data.feed.entry.slice(0, showCount);

  return (
    <div
      className={cn(
        'grid relative w-full h-fit',
        className,
        isLoading && 'opacity-50 animate-pulse',
        COLUMNS_MAP[columns || '3'],
      )}
      style={{
        gap: gap || '16px',
        padding: padding,
      }}
      {...attributes}
    >
      {shownData.map((entry) => (
        <YouTubeRssFeedCard key={entry.id} entry={entry} element={element} attributes={attributes} onClick={onClick} />
      ))}
      {children}
    </div>
  );
};
